/**
 * TransformSpec 编译器：
 * - compileSpecToLegacyParser：P1（数据集）→ 现有 path/mapping/filter/transform
 * - compileSpecToJs：P2（设备）→ 现有沙箱脚本（function parse）
 *
 * 输出必须确定性（同一 spec 两次编译逐字节一致），供"可视化 ↔ 代码"互斥切换使用。
 */

import type { DataFieldMapping } from '../types';
import {
  HELPER_PRELUDE,
  collectIdentifiers,
  compileExpression,
  parseExpression,
  type ExprNode,
} from './expression';
import {
  validateTransformSpec,
  type ComputeRule,
  type ConditionGroup,
  type ConditionItem,
  type FieldRule,
  type FieldType,
  type TransformSpec,
  type UnitRule,
} from './spec';

export interface LegacyParser {
  path?: string;
  mapping?: DataFieldMapping[];
  filter?: string;
  transform?: string;
}

function indent(text: string, spaces: number): string {
  const pad = ' '.repeat(spaces);
  return text
    .split('\n')
    .map((line) => (line ? pad + line : line))
    .join('\n');
}

function jsonLit(v: unknown): string {
  if (v === undefined) return 'undefined';
  return JSON.stringify(v);
}

function legacyType(type?: FieldType): DataFieldMapping['type'] {
  switch (type) {
    case 'number':
      return 'number';
    case 'string':
      return 'string';
    case 'date':
      return 'date';
    default:
      return undefined;
  }
}

function typeCode(code: string, type?: FieldType): string {
  switch (type) {
    case 'number':
      return `_num(${code})`;
    case 'string':
      return `String(${code})`;
    case 'boolean':
      return `_bool(${code})`;
    case 'date':
      return `_date(${code})`;
    default:
      return code;
  }
}

function unitCode(code: string, unit?: UnitRule): string {
  if (!unit || unit.kind === 'none') return code;
  if (unit.kind === 'cToF') return `_cToF(${code})`;
  if (unit.kind === 'fToC') return `_fToC(${code})`;
  return `_scale(${code}, ${unit.scale ?? 1}, ${unit.offset ?? 0})`;
}

function decimalsCode(code: string, decimals?: number): string {
  if (decimals === undefined) return code;
  return `_round(${code}, ${decimals})`;
}

function collectFields(spec: TransformSpec): {
  mapped: FieldRule[];
  computed: ComputeRule[];
  filters: ConditionGroup[];
  renames: Array<{ from: string; to: string }>;
  drops: string[];
  limit?: number;
} {
  const mapped: FieldRule[] = [];
  const computed: ComputeRule[] = [];
  const filters: ConditionGroup[] = [];
  const renames: Array<{ from: string; to: string }> = [];
  const drops: string[] = [];
  let limit: number | undefined;
  for (const step of spec.steps) {
    switch (step.op) {
      case 'map':
        mapped.push(...step.fields);
        break;
      case 'compute':
        computed.push(...step.fields);
        break;
      case 'filter':
        filters.push(step.cond);
        break;
      case 'rename':
        renames.push({ from: step.from, to: step.to });
        break;
      case 'drop':
        drops.push(...step.fields);
        break;
      case 'limit':
        limit = step.count;
        break;
      default:
        break;
    }
  }
  return { mapped, computed, filters, renames, drops, limit };
}

function conditionCode(
  cond: ConditionGroup,
  ident: (name: string) => string | undefined,
  meta: Record<string, string>,
): string {
  const parts = cond.items.map((item) => {
    if ((item as ConditionGroup).items !== undefined) {
      return `(${conditionCode(item as ConditionGroup, ident, meta)})`;
    }
    const it = item as ConditionItem;
    const left = ident(it.field);
    if (left === undefined) throw new Error(`过滤字段「${it.field}」未定义`);
    switch (it.op) {
      case 'eq':
        return `(${left} === ${jsonLit(it.value)})`;
      case 'neq':
        return `(${left} !== ${jsonLit(it.value)})`;
      case 'gt':
        return `(${left} > ${jsonLit(it.value)})`;
      case 'gte':
        return `(${left} >= ${jsonLit(it.value)})`;
      case 'lt':
        return `(${left} < ${jsonLit(it.value)})`;
      case 'lte':
        return `(${left} <= ${jsonLit(it.value)})`;
      case 'contains':
        return `(String(${left}).indexOf(String(${jsonLit(it.value)})) >= 0)`;
      case 'in': {
        const arr = Array.isArray(it.value) ? it.value : [];
        return `([${arr.map(jsonLit).join(', ')}].indexOf(${left}) >= 0)`;
      }
      case 'exists':
        return `(${left} !== undefined && ${left} !== null)`;
      default:
        throw new Error(`不支持的操作符「${String(it.op)}」`);
    }
  });
  return parts.join(cond.logic === 'or' ? ' || ' : ' && ');
}

function compileExpr(
  src: string,
  ident: (name: string) => string | undefined,
  meta: Record<string, string>,
): string {
  const node: ExprNode = parseExpression(src);
  return compileExpression(node, { ident, meta });
}

function assertSpec(spec: TransformSpec, mode: 'rows' | 'object'): void {
  const check = validateTransformSpec(spec);
  if (!check.ok) throw new Error(check.errors.join('；'));
  if (spec.mode !== mode) {
    throw new Error(mode === 'rows' ? '数据集解析规格 mode 需为 rows' : '设备解析规格 mode 需为 object');
  }
}

// ============================================================
// P1：编译为现有 parser 契约
// ============================================================

export function compileSpecToLegacyParser(spec: TransformSpec): LegacyParser {
  assertSpec(spec, 'rows');
  const { mapped, computed, filters, renames, drops, limit } = collectFields(spec);

  const out: LegacyParser = {};
  if (spec.source?.path) out.path = spec.source.path;

  if (mapped.length) {
    out.mapping = mapped.map((f) => {
      const item: DataFieldMapping = {
        sourceField: f.from,
        targetField: f.to,
      };
      const t = legacyType(f.type);
      if (t) item.type = t;
      if (f.decimals !== undefined) item.format = String(f.decimals);
      else if (f.format) item.format = f.format;
      if (f.unit && f.unit.kind !== 'none') item.unit = f.unit;
      return item;
    });
  }

  if (filters.length) {
    const allowed = new Set(mapped.map((f) => f.to));
    const codes = filters.map((cond) =>
      conditionCode(
        cond,
        (name) => (allowed.has(name) ? `item.${name}` : undefined),
        { $index: 'index', $row: 'item' },
      ),
    );
    out.filter = codes.map((c) => `(${c})`).join(' && ');
  }

  const needsTransform = computed.length > 0 || renames.length > 0 || drops.length > 0 || limit !== undefined;
  if (needsTransform) {
    const allowed = new Set([...mapped.map((f) => f.to), ...computed.map((f) => f.to)]);
    const body: string[] = [];
    for (const r of renames) {
      body.push(`o.${r.to} = o.${r.from};`);
      body.push(`delete o.${r.from};`);
    }
    for (const d of drops) body.push(`delete o.${d};`);
    for (const c of computed) {
      const expr = compileExpr(
        c.expr,
        (name) => (allowed.has(name) ? `o.${name}` : undefined),
        { $index: 'i', $row: 'o' },
      );
      body.push(`o.${c.to} = ${expr};`);
      if (c.type) body.push(`o.${c.to} = ${typeCode(`o.${c.to}`, c.type)};`);
      if (c.decimals !== undefined) body.push(`o.${c.to} = ${decimalsCode(`o.${c.to}`, c.decimals)};`);
    }
    const mapLines = [
      'return data.map(function (r, i) {',
      '  var o = Object.assign({}, r);',
      ...body.map((line) => `  ${line}`),
      '  return o;',
      `})${limit !== undefined ? `.slice(0, ${limit})` : ''};`,
    ];
    out.transform = [
      '(function () {',
      indent(HELPER_PRELUDE, 2),
      indent(mapLines.join('\n'), 2),
      '})()',
    ].join('\n');
  }

  return out;
}

// ============================================================
// P2：编译为沙箱脚本 function parse(payload, meta)
// ============================================================

function outputKeys(spec: TransformSpec, all: string[]): {
  points: string[];
  io?: string[];
  network?: string[];
} {
  if (!spec.output) return { points: all };
  return {
    points: spec.output.points ?? all,
    io: spec.output.io,
    network: spec.output.network,
  };
}

export function compileSpecToJs(spec: TransformSpec): string {
  assertSpec(spec, 'object');
  const { mapped, computed, filters, renames, drops } = collectFields(spec);
  const allowed = new Set([...mapped.map((f) => f.to), ...computed.map((f) => f.to)]);
  const lines: string[] = [];

  lines.push('function parse(payload, meta) {');
  lines.push('  meta = meta || {};');
  lines.push("  if (typeof payload === 'string') { try { payload = JSON.parse(payload); } catch (e) {} }");
  lines.push(indent(HELPER_PRELUDE, 2));
  lines.push('  var out = {};');
  const basePath = typeof spec.source?.path === 'string' ? spec.source.path.trim().replace(/^\.+|\.+$/g, '') : '';

  for (const f of mapped) {
    const from = basePath && f.from !== basePath && !f.from.startsWith(`${basePath}.`) && !f.from.startsWith(`${basePath}[`)
      ? `${basePath}.${f.from}`
      : f.from;
    lines.push(`  out.${f.to} = _get(payload, ${jsonLit(from)});`);
    if (f.fallback !== undefined) {
      lines.push(`  if (out.${f.to} === undefined) out.${f.to} = ${jsonLit(f.fallback)};`);
    }
    if (f.type) lines.push(`  out.${f.to} = ${typeCode(`out.${f.to}`, f.type)};`);
    if (f.unit && f.unit.kind !== 'none') {
      lines.push(`  out.${f.to} = ${unitCode(`out.${f.to}`, f.unit)};`);
    }
    if (f.decimals !== undefined) {
      lines.push(`  out.${f.to} = ${decimalsCode(`out.${f.to}`, f.decimals)};`);
    }
  }

  for (const r of renames) {
    lines.push(`  out.${r.to} = out.${r.from};`);
    lines.push(`  delete out.${r.from};`);
  }
  for (const d of drops) lines.push(`  delete out.${d};`);

  for (const cond of filters) {
    const code = conditionCode(
      cond,
      (name) => (allowed.has(name) ? `out.${name}` : undefined),
      { $meta: 'meta' },
    );
    lines.push(`  if (!(${code})) return {};`);
  }

  for (const c of computed) {
    const expr = compileExpr(
      c.expr,
      (name) => (allowed.has(name) ? `out.${name}` : undefined),
      { $meta: 'meta' },
    );
    lines.push(`  out.${c.to} = ${expr};`);
    if (c.type) lines.push(`  out.${c.to} = ${typeCode(`out.${c.to}`, c.type)};`);
    if (c.decimals !== undefined) lines.push(`  out.${c.to} = ${decimalsCode(`out.${c.to}`, c.decimals)};`);
  }

  const all = [...mapped.map((f) => f.to), ...computed.map((f) => f.to)];
  const keys = outputKeys(spec, all);
  lines.push('  var points = {};');
  for (const k of keys.points) lines.push(`  points.${k} = out.${k};`);
  lines.push('  var result = { points: points };');
  if (keys.io && keys.io.length) {
    lines.push('  var io = {};');
    for (const k of keys.io) lines.push(`  io.${k} = out.${k};`);
    lines.push('  result.io = io;');
  }
  if (keys.network && keys.network.length) {
    lines.push('  var network = {};');
    for (const k of keys.network) lines.push(`  network.${k} = out.${k};`);
    lines.push('  result.network = network;');
  }
  lines.push('  return result;');
  lines.push('}');
  return lines.join('\n') + '\n';
}

/** 代码是否正是由该 spec 编译而来（逐字节比对，用于严格互斥切换） */
export function isCompiledFromSpec(spec: TransformSpec, script: string): boolean {
  try {
    return compileSpecToJs(spec) === script;
  } catch {
    return false;
  }
}

// ============================================================
// 模板（UI 一键填充）
// ============================================================

export interface SpecTemplate {
  id: string;
  name: string;
  description: string;
  spec: TransformSpec;
}

export const P1_TEMPLATES: SpecTemplate[] = [
  {
    id: 'p1-flat-array',
    name: '平铺数组',
    description: 'data.items[] 中的 ts/value → 时间/数值',
    spec: {
      v: 1,
      mode: 'rows',
      source: { path: 'data.items' },
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'ts', to: 'time', type: 'date' },
            { from: 'value', to: 'value', type: 'number', decimals: 1 },
          ],
        },
      ],
    },
  },
  {
    id: 'p1-nested',
    name: '嵌套对象',
    description: 'data.items[] 中的 meta.ts / meta.value',
    spec: {
      v: 1,
      mode: 'rows',
      source: { path: 'data.items' },
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'meta.ts', to: 'time', type: 'date' },
            { from: 'meta.value', to: 'value', type: 'number', decimals: 2 },
          ],
        },
      ],
    },
  },
  {
    id: 'p1-unit',
    name: '单位换算',
    description: '原始值 ×0.1 并保留 1 位小数',
    spec: {
      v: 1,
      mode: 'rows',
      source: { path: 'data' },
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'temp', to: 'temp', type: 'number', unit: { kind: 'scale', scale: 0.1 }, decimals: 1 },
            { from: 'hum', to: 'humidity', type: 'number' },
          ],
        },
        {
          op: 'compute',
          fields: [{ to: 'tempF', expr: 'cToF(temp)', type: 'number', decimals: 1 }],
        },
      ],
    },
  },
  {
    id: 'p1-filter',
    name: '过滤 + 分级',
    description: '只保留 value>3，并生成 level 字段',
    spec: {
      v: 1,
      mode: 'rows',
      source: { path: 'data.items' },
      steps: [
        { op: 'map', fields: [{ from: 'value', to: 'value', type: 'number', decimals: 1 }] },
        { op: 'filter', cond: { logic: 'and', items: [{ field: 'value', op: 'gt', value: 3 }] } },
        {
          op: 'compute',
          fields: [{ to: 'level', expr: "if(value > 8, 'high', 'low')", type: 'string' }],
        },
      ],
    },
  },
  {
    id: 'p1-timeseries',
    name: '时间序列',
    description: 'list[] 的 ts/v → time/value（适合折线图）',
    spec: {
      v: 1,
      mode: 'rows',
      source: { path: 'list' },
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'ts', to: 'time', type: 'date' },
            { from: 'v', to: 'value', type: 'number', decimals: 2 },
          ],
        },
        { op: 'limit', count: 500 },
      ],
    },
  },
];

export const P2_TEMPLATES: SpecTemplate[] = [
  {
    id: 'p2-rtu-v1',
    name: 'RTU v1.0（points/io）',
    description: 'data.points.temp + data.io.ai0，计算 tempF',
    spec: {
      v: 1,
      mode: 'object',
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'data.points.temp', to: 'temp', type: 'number', decimals: 1 },
            { from: 'data.io.ai0', to: 'ai0', type: 'number', fallback: 0 },
          ],
        },
        {
          op: 'compute',
          fields: [{ to: 'tempF', expr: 'cToF(temp)', type: 'number', decimals: 1 }],
        },
      ],
      output: { points: ['temp', 'tempF'], io: ['ai0'] },
    },
  },
  {
    id: 'p2-rtu-v2',
    name: 'RTU v2.0（data.p）',
    description: 'data.p.temp（×0.1）/ data.p.hum',
    spec: {
      v: 1,
      mode: 'object',
      steps: [
        {
          op: 'map',
          fields: [
            {
              from: 'data.p.temp',
              to: 'temp',
              type: 'number',
              unit: { kind: 'scale', scale: 0.1 },
              decimals: 1,
            },
            { from: 'data.p.hum', to: 'humidity', type: 'number' },
          ],
        },
      ],
      output: { points: ['temp', 'humidity'] },
    },
  },
  {
    id: 'p2-modbus',
    name: 'Modbus 数组',
    description: 'data.list[0].v / data.list[1].v',
    spec: {
      v: 1,
      mode: 'object',
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'data.list[0].v', to: 'temp', type: 'number', decimals: 1 },
            { from: 'data.list[1].v', to: 'humidity', type: 'number' },
          ],
        },
      ],
      output: { points: ['temp', 'humidity'] },
    },
  },
  {
    id: 'p2-flat',
    name: '扁平 JSON',
    description: 'temp/humidity 直接在报文根节点',
    spec: {
      v: 1,
      mode: 'object',
      steps: [
        {
          op: 'map',
          fields: [
            { from: 'temp', to: 'temp', type: 'number', decimals: 1 },
            { from: 'humidity', to: 'humidity', type: 'number' },
          ],
        },
      ],
      output: { points: ['temp', 'humidity'] },
    },
  },
];

/** 从样例报文推断字段路径（UI「从样例推断」用；最多 60 个叶子） */
export function inferFieldPaths(sample: unknown, max = 60): string[] {
  const out: string[] = [];
  const walk = (value: unknown, prefix: string): void => {
    if (out.length >= max) return;
    if (value === null || value === undefined) {
      if (prefix) out.push(prefix);
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        if (prefix) out.push(prefix);
        return;
      }
      walk(value[0], prefix ? `${prefix}[0]` : '[0]');
      return;
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (out.length >= max) return;
        walk(v, prefix ? `${prefix}.${k}` : k);
      }
      return;
    }
    if (prefix) out.push(prefix);
  };
  walk(sample, '');
  return out;
}

/** 校验表达式（UI 实时提示用） */
export function validateExpressionText(src: string, allowedFields: string[]): string | null {
  try {
    const node = parseExpression(src);
    const allowed = new Set([...allowedFields, '$index', '$row', '$meta']);
    const unknown = [...collectIdentifiers(node)].filter((n) => !allowed.has(n));
    if (unknown.length) return `未知字段「${unknown[0]}」`;
    compileExpression(node, { ident: (n) => n, meta: {} });
    return null;
  } catch (e: any) {
    return e?.message || '表达式非法';
  }
}
